import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import useAuthStore from './store/authStore';

// Pages
import LoginPage from './pages/LoginPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PasswordResetConfirmPage from './pages/PasswordResetConfirmPage';
import DashboardPage from './pages/DashboardPage';
import UnauthorizedPage from './pages/UnauthorizedPage';
import CSVUploadPage from './pages/CSVUploadPage';
import CSVUploadWithQuestionsPage from './pages/CSVUploadWithQuestionsPage';
import CSVDemoPage from './pages/CSVDemoPage';
import BrandMaintenancePage from './pages/BrandMaintenancePage';
import MarketMaintenancePage from './pages/MarketMaintenancePage';
import DealerGroupMaintenancePage from './pages/DealerGroupMaintenancePage';
import DragDropDealerGroupPage from './pages/DragDropDealerGroupPage';
import FieldMappingPage from './pages/FieldMappingPage';
import FinancialDataPage from './pages/FinancialDataPage';
import AnalyticsPage from './pages/AnalyticsPage';
import KPIAnalyticsPage from './pages/KPIAnalyticsPage';
import VisuAIDataViewerPage from './pages/VisuAIDataViewerPage';
import UserMaintenancePage from './pages/UserMaintenancePage';
import GroupUserManagementPage from './pages/GroupUserManagementPage';
import RoleMaintenancePage from './pages/RoleMaintenancePage';
import KPIMaintenancePage from './pages/KPIMaintenancePage';
import GenerateAIValidationsPage from './pages/GenerateAIValidationsPage';
import UserQuestionsPage from './pages/UserQuestionsPage';
import SuperAdminPage from './pages/SuperAdminPage';
import LocationManagementPage from './pages/LocationManagementPage';
import HierarchyManagementPage from './pages/HierarchyManagementPage';
import UserAccessManagementPage from './pages/UserAccessManagementPage';
import LocationHierarchyPage from './pages/LocationHierarchyPage';
import OrganisationKPIsPage from './pages/OrganisationKPIsPage';
import SubmitDataPage from './pages/SubmitDataPage';
import SubmissionQualityCheckPage from './pages/SubmissionQualityCheckPage';
import ViewSubmissionsPage from './pages/ViewSubmissionsPage';
import OrganisationManagementPage from './pages/OrganisationManagementPage';
import OrganisationKPIRulesPage from './pages/OrganisationKPIRulesPage';
import QuarterlyReportsPage from './pages/QuarterlyReportsPage';
import DashboardConfigurationPage from './pages/DashboardConfigurationPage';
import ExecutiveSearchPage from './pages/ExecutiveSearchPage';
import ExecutiveChatPage from './pages/ExecutiveChatPage';
import { ExecutiveXLSXChatPage } from './pages/ExecutiveXLSXChatPage';
import { UniverTestPage } from './pages/UniverTestPage';
import SalesDataDemoPage from './pages/SalesDataDemoPage';
import ManufacturerSalesImportPage from './pages/ManufacturerSalesImportPage';
import RentalUtilizationImportPage from './pages/RentalUtilizationImportPage';
import GoogleReviewsOpsPage from './pages/GoogleReviewsOpsPage';
import GoogleReviewsDashboardPage from './pages/GoogleReviewsDashboardPage';

// Test Components
import KPIModelTest from './components/test/KPIModelTest';

// Components
import ProtectedRoute from './components/auth/ProtectedRoute';

// State
import useFeatureFlagStore from './store/featureFlagStore';

// Theme Context
import { ThemeProvider } from './contexts/ThemeContext';

const App: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuthStore();
  const [isInitializing, setIsInitializing] = useState(true);

  // Initialize auth state and feature flags from localStorage when the app loads
  useEffect(() => {
    const initializeApp = async () => {
      console.log('🚀 App initializing...');
      
      // Ensure auth state is initialized when component mounts
      await useAuthStore.getState().initializeFromStorage();
      
      const authState = useAuthStore.getState();
      console.log('🔍 Auth state after initialization:', {
        isAuthenticated: authState.isAuthenticated,
        user: authState.user?.name,
        hasToken: !!localStorage.getItem('token')
      });
      
      // Initialize feature flags after auth state is loaded
      if (authState.isAuthenticated) {
        console.log('✅ User authenticated, initializing feature flags');
        useFeatureFlagStore.getState().initializeWithUser();
      } else {
        console.log('❌ User not authenticated');
      }
      
      setIsInitializing(false);
      console.log('✅ App initialization complete');
    };

    initializeApp();
  }, []);

  // Show loading spinner while initializing
  if (isInitializing || isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <div style={{ 
          color: 'white', 
          fontSize: '18px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <div style={{
            width: '20px',
            height: '20px',
            border: '2px solid #ffffff40',
            borderTop: '2px solid white',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          Loading...
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <ThemeProvider>
      <div className="App">
          {/* HUD Background Effects */}
          <div className="hud-data-nodes">
            <div className="data-node"></div>
            <div className="data-node"></div>
            <div className="data-node"></div>
            <div className="data-node"></div>
          </div>
          <div className="hud-scan-lines">
            <div className="scan-line"></div>
            <div className="scan-line"></div>
          </div>
        
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={
            isAuthenticated ? <Navigate to={useAuthStore.getState().user?.landing_page || "/dashboard"} /> : <LoginPage />
          } />
          <Route path="/reset-password" element={
            isAuthenticated ? <Navigate to="/dashboard" /> : <ResetPasswordPage />
          } />
          <Route path="/reset-password/:token" element={
            isAuthenticated ? <Navigate to="/dashboard" /> : <PasswordResetConfirmPage />
          } />
          <Route path="/unauthorized" element={<UnauthorizedPage />} />

          {/* Protected routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute element={<DashboardPage />} featureKey="dashboard" />
          } />
          <Route path="/upload-csv" element={
            <ProtectedRoute element={<CSVUploadPage />} featureKey="upload_csv" />
          } />
          <Route path="/upload-csv-questions" element={
            <ProtectedRoute element={<CSVUploadWithQuestionsPage />} featureKey="upload_csv_questions" />
          } />
          <Route path="/csv-demo" element={
            <ProtectedRoute element={<CSVDemoPage />} featureKey="csv_demo" />
          } />
          <Route path="/brand-maintenance" element={
            <ProtectedRoute element={<BrandMaintenancePage />} featureKey="brand_maintenance" />
          } />
          <Route path="/market-maintenance" element={
            <ProtectedRoute element={<MarketMaintenancePage />} featureKey="market_maintenance" />
          } />
          <Route path="/dealer-group-maintenance" element={
            <ProtectedRoute element={<DealerGroupMaintenancePage />} featureKey="dealer_group_maintenance" />
          } />
          <Route path="/kpi-maintenance" element={
            <ProtectedRoute element={<KPIMaintenancePage />} featureKey="kpi_maintenance" />
          } />
          <Route path="/user-questions" element={
            <ProtectedRoute element={<UserQuestionsPage />} featureKey="user_questions" />
          } />
          <Route path="/dealer-group-drag-drop" element={
            <ProtectedRoute element={<DragDropDealerGroupPage />} featureKey="dealer_group_maintenance" />
          } />
          <Route path="/map-data" element={
            <ProtectedRoute element={<FieldMappingPage />} featureKey="map_data" />
          } />
          <Route path="/financial-data" element={
            <ProtectedRoute element={<FinancialDataPage />} featureKey="financial_data" />
          } />
          <Route path="/analytics" element={
            <ProtectedRoute element={<AnalyticsPage />} featureKey="analytics" />
          } />
          <Route path="/kpi-analytics" element={
            <ProtectedRoute element={<KPIAnalyticsPage />} featureKey="kpi_analytics" />
          } />
          <Route path="/generate-ai-validations" element={
            <ProtectedRoute element={<GenerateAIValidationsPage />} featureKey="generate_ai_validations" />
          } />
          <Route path="/sales-data-demo" element={
            <ProtectedRoute element={<SalesDataDemoPage />} featureKey="sales_data_demo" />
          } />
          <Route path="/manufacturer-sales-import" element={
            <ProtectedRoute element={<ManufacturerSalesImportPage />} featureKey="manufacturer_sales_import" />
          } />
          <Route path="/rental-utilization-import" element={
            <ProtectedRoute element={<RentalUtilizationImportPage />} featureKey="rental_utilization_import" />
          } />
          <Route path="/google-reviews/ops" element={
            <ProtectedRoute element={<GoogleReviewsOpsPage />} featureKey="google_reviews_ops" />
          } />
          <Route path="/google-reviews/dashboard" element={
            <ProtectedRoute element={<GoogleReviewsDashboardPage />} featureKey="google_reviews_dashboard" />
          } />
          <Route path="/visuai-data-viewer" element={
            <ProtectedRoute element={<VisuAIDataViewerPage />} featureKey="visuai_data_viewer" />
          } />
          <Route path="/user-maintenance" element={
            <ProtectedRoute element={<UserMaintenancePage />} requiredRole="admin" featureKey="user_maintenance" />
          } />
          <Route path="/group-user-management" element={
            <ProtectedRoute element={<GroupUserManagementPage />} requiredRole="group_admin" featureKey="group_user_maintenance" />
          } />
          <Route path="/role-maintenance" element={
            <ProtectedRoute element={<RoleMaintenancePage />} requiredRole="admin" featureKey="role_maintenance" />
          } />
          <Route path="/super-admin" element={
            <ProtectedRoute element={<SuperAdminPage />} />
          } />
          <Route path="/location-management" element={
            <ProtectedRoute element={<LocationManagementPage />} featureKey="location_management" />
          } />
          <Route path="/hierarchy-management" element={
            <ProtectedRoute element={<HierarchyManagementPage />} featureKey="hierarchy_management" />
          } />
          <Route path="/user-access-management" element={
            <ProtectedRoute element={<UserAccessManagementPage />} featureKey="user_access_management" />
          } />
          <Route path="/location-hierarchy" element={
            <ProtectedRoute element={<LocationHierarchyPage />} requiredRole="admin" featureKey="location_hierarchy" />
          } />
          <Route path="/organisation-kpis" element={
            <ProtectedRoute element={<OrganisationKPIsPage />} featureKey="organisation_kpis" />
          } />
          <Route path="/submit-data" element={
            <ProtectedRoute element={<SubmitDataPage />} featureKey="submit_data" />
          } />
          <Route path="/submission-quality-check" element={
            <ProtectedRoute element={<SubmissionQualityCheckPage />} featureKey="submission_quality_check" />
          } />
          <Route path="/view-submissions" element={
            <ProtectedRoute element={<ViewSubmissionsPage />} featureKey="view_submissions" />
          } />
          <Route path="/organisation-management" element={
            <ProtectedRoute element={<OrganisationManagementPage />} featureKey="organisation_management" />
          } />
          <Route path="/organisation-kpi-rules" element={
            <ProtectedRoute element={<OrganisationKPIRulesPage />} featureKey="organisation_kpi_rules" />
          } />
          <Route path="/quarterly-reports" element={
            <ProtectedRoute element={<QuarterlyReportsPage />} featureKey="quarterly_reports" />
          } />
          <Route path="/dashboard-config" element={
            <ProtectedRoute element={<DashboardConfigurationPage />} featureKey="dashboard_configuration" />
          } />
          <Route path="/dashboard-config/:dashboardId" element={
            <ProtectedRoute element={<DashboardConfigurationPage />} featureKey="dashboard_configuration" />
          } />
          <Route path="/executive-search" element={
            <ProtectedRoute element={<ExecutiveSearchPage />} featureKey="executive_search" />
          } />
          <Route path="/executive-chat" element={
            <ProtectedRoute element={<ExecutiveChatPage />} featureKey="executive_chat" />
          } />
          <Route path="/executive-xlsx-chat" element={
            <ProtectedRoute element={<ExecutiveXLSXChatPage />} featureKey="executive_xlsx_chat" />
          } />

          {/* Test Routes - Remove after testing */}
          <Route path="/univer-test" element={<UniverTestPage />} />
          <Route path="/test-kpi-model" element={
            <ProtectedRoute element={<KPIModelTest />} featureKey="dashboard" />
          } />

          {/* Redirect to login or dashboard based on auth state */}
          <Route path="/" element={
            isAuthenticated ? <Navigate to={useAuthStore.getState().user?.landing_page || "/dashboard"} /> : <Navigate to="/login" />
          } />

          {/* 404 route - redirect to dashboard or login */}
          <Route path="*" element={
            isAuthenticated ? <Navigate to={useAuthStore.getState().user?.landing_page || "/dashboard"} /> : <Navigate to="/login" />
          } />
        </Routes>
      </div>
    </ThemeProvider>
  );
};

export default App;
